import { ChangeDetectorRef, Directive, ElementRef, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { of, Subscription } from 'rxjs'
import {
  Address,
  Country,
  EventService,
  isNotNullable,
  Region,
  SiteConnector,
  WindowRef
} from '@spartacus/core'
import { MbConstants } from '../../../../shared/mb-constants'
import { ActivatedRoute } from '@angular/router'
import { MbCheckoutStepService } from '../../../services/mb-checkout-step.service'
import { MbCheckoutCommonService } from '../../../services/mb-checkout-common.service'
import { MbCheckoutBillingFacade } from '../../../facades/mb-checkout-billing.facade'
import { filter, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { MbBillingInformation, MbCheckoutTypes, MbPaymentModeSelection } from '../../../../models'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { CheckoutBillingAddressSetEvent } from '../../../../analytics/events/mb-checkout.events'

@Directive()
export abstract class MbCheckoutBillingAddressFormBase implements OnInit, OnDestroy {

  billingForm: UntypedFormGroup = this.convertCheckoutBillingInformationModel2Form()
  billingAddress: Address = {}
  paymentModeSelection: MbPaymentModeSelection = {}
  billingInformation: MbBillingInformation = {
    billingAddress: this.billingAddress,
    paymentModeSelection: this.paymentModeSelection
  } as MbBillingInformation
  shippingAddress: Address | undefined
  subscription = new Subscription()
  constants = MbConstants
  country: Country | undefined
  regions: Region[] = []
  isCardViewVisible = true
  selectedState: Region | undefined = { name: '', isocode: '' }
  shouldLeavePage = false
  displaySimpleButtons = false
  isPurchaseCheckoutType = false
  checkoutTypes = MbCheckoutTypes

  constructor (protected activatedRoute: ActivatedRoute,
               protected mbCheckoutStepService: MbCheckoutStepService,
               protected siteConnector: SiteConnector,
               protected activeCartService: ActiveCartFacade,
               protected mbCheckoutCommonService: MbCheckoutCommonService,
               protected mbCheckoutBillingFacade: MbCheckoutBillingFacade,
               protected cdRef: ChangeDetectorRef,
               protected winRef: WindowRef,
               protected el: ElementRef,
               protected eventService: EventService) { }

  ngOnInit () {
    this.subscription?.add(this.mbCheckoutCommonService.userCountry.pipe(
        filter(isNotNullable),
        switchMap(userCountry => {
          this.country = userCountry
          this.setCountry()
          this.billingForm = this.convertCheckoutBillingInformationModel2Form()
          if (userCountry?.isocode) {
            return this.siteConnector.getRegions(userCountry?.isocode)
          } else {
            return of([])
          }
        })).subscribe(regions => {
        if (regions) {
          this.regions = [...regions]
        }
      })
    )
    this.subscription?.add(this.activeCartService.getActive().pipe(
      withLatestFrom(this.activeCartService.isGuestCart())
    ).subscribe(([cart, isGuestCart]) => {
      if (!cart) {
        return
      }
      if (this.shouldLeavePage && !this.isCardViewVisible) {
        this.shouldLeavePage = false
        this.mbCheckoutStepService.next(this.activatedRoute)
      }
      this.shippingAddress = cart.deliveryAddress

      const checkoutType = cart.checkoutStatus?.checkoutType
      if (checkoutType === this.checkoutTypes.CHECKOUT_TYPE_D || checkoutType === this.checkoutTypes.CHECKOUT_TYPE_E) {
        this.isPurchaseCheckoutType = true
      }

      if (isGuestCart) {
        this.country = cart.country

        this.billingAddress = {
          ...this.billingAddress,
          ...cart.paymentDetails?.billingAddress
        }
        this.billingInformation = {
          ...this.billingInformation,
          billingAddress: this.billingAddress,
          paymentModeSelection: cart.paymentDetails?.paymentModeSelection
        }
        if (cart.paymentDetails?.billingAddress?.region?.isocode) {
          this.selectedState = cart.paymentDetails?.billingAddress?.region
        }
        this.billingForm = this.convertCheckoutBillingInformationModel2Form()
      }
      }))
    this.subscription?.add(this.mbCheckoutBillingFacade.selectedPaymentModeSelection
      .subscribe(paymentMode => {
        this.paymentModeSelection = paymentMode
        if (!Object.keys(this.paymentModeSelection)?.length) {
          this.paymentModeSelection = { paymentMode: { code: this.constants.PLACEHOLDER_PAYMENT_MODE_CODE } }
        }
        this.convertCheckoutBillingInformationForm2Model()
      }))
    this.subscription?.add(this.eventService.get(CheckoutBillingAddressSetEvent).subscribe(payload => {
      this.shouldLeavePage = !!payload?.isSuccessful
      this.activeCartService.reloadActiveCart()
      })
    )
    this.subscription?.add(this.mbCheckoutBillingFacade.isCardViewAndPaymentVisible.pipe(
      tap(showCardView => this.isCardViewVisible = showCardView)
    ).subscribe())
    this.subscription?.add(this.mbCheckoutBillingFacade.isDisplaySimpleButtons.pipe(
      tap(isDisplaySimpleButtons => this.displaySimpleButtons = isDisplaySimpleButtons)
    ).subscribe())
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  saveAndContinue () {
    if (!this.billingForm.valid) {
      this.billingForm.markAllAsTouched()
      this.mbCheckoutCommonService.scrollToError(this.billingForm, this.el, this.winRef)
    } else {
      this.postForm()
    }
  }

  cancel () {
    if (!this.displaySimpleButtons) {
      // TODO: post form
      this.mbCheckoutStepService.back(this.activatedRoute)
    } else {
      this.mbCheckoutBillingFacade.openCardView()
    }
    this.winRef.nativeWindow?.scroll({ top: 0, behavior: 'smooth' })
  }

  onStateSelection (value?: Region) {
    this.selectedState = value
    this.billingForm.patchValue({ region: value })
  }

  prefillActivate () {
    this.convert(this.shippingAddress, this.billingAddress)
    this.billingForm = this.convertCheckoutBillingInformationModel2Form()
  }

  private postForm () {
    this.convertCheckoutBillingInformationForm2Model()
    this.mbCheckoutBillingFacade.postBillingStepForm(this.billingInformation)
  }

  isCountryDisabled () {
    if (this.country) {
      this.billingForm?.patchValue({ country: this.country?.name })
      this.billingForm?.get(this.constants.COUNTRY_CODE)?.disable()
      return true
    } else {
      this.billingForm?.patchValue({ country: { name: this.constants.COUNTRY, isocode: '' } })
      this.billingForm?.get(this.constants.COUNTRY_CODE)?.enable()
      return false
    }
  }

  setCountry () {
    this.billingAddress.country = this.country
    this.billingForm.patchValue({ country: this.country })
    this.isCountryDisabled()
    this.convertCheckoutBillingInformationModel2Form()
  }

  abstract convert(source?: Address, target?: Address): void

  abstract convertCheckoutBillingInformationModel2Form(): UntypedFormGroup

  abstract convertCheckoutBillingInformationForm2Model(): void
}
